export type ListKeyAction = {
    kind: 'focus' | 'click';
    index: number;
};

export interface ListRow {
    kind: string;
}

export interface SectionTitleRow extends ListRow {
    kind: 'sectionTitle';
    isOpen: boolean;
    canToggle: boolean;
}

export type RowIsFocusable<Row extends ListRow> = (row: Row) => boolean;

export type RowKey<Row> = (row: Row) => string | null;

// A key for each row that no other row has: react-window keys the row
// elements by it, and the Tab stop and the highlight name their row by it. A
// list's row key already keeps the copies of one item apart, such as a
// favourite and its row in its section. A row without a key of its own is
// named by its kind. If a key repeats, the second row gets "#2".
export const uniqueRowKeys = <Row extends ListRow>(
    rows: Row[],
    rowKey: RowKey<Row>,
) => {
    const timesSeen = new Map<string, number>();

    return rows.map((row) => {
        const key = rowKey(row) ?? row.kind;
        const seen = timesSeen.get(key) ?? 0;
        timesSeen.set(key, seen + 1);
        return seen === 0 ? key : `${key}#${seen + 1}`;
    });
};

// react-window's itemKey for a list whose item data holds the rows' keys.
export const listItemKey = (index: number, data: { rowKeys: string[] }) =>
    data.rowKeys[index];

// The index of the row with this key. Searches outward from the row's last
// index, so a row that has not moved costs one step.
export const indexOfKeyNear = (
    rowKeys: string[],
    { key, index }: { key: string; index: number },
) => {
    const keyIsAt = (at: number) =>
        at >= 0 && at < rowKeys.length && rowKeys[at] === key;
    const lastDistance = Math.max(index, rowKeys.length - 1 - index);
    for (let distance = 0; distance <= lastDistance; distance += 1) {
        if (keyIsAt(index + distance)) {
            return index + distance;
        }
        if (distance > 0 && keyIsAt(index - distance)) {
            return index - distance;
        }
    }
    return -1;
};

const isSectionTitle = <Row extends ListRow>(
    row: Row,
): row is Row & SectionTitleRow => row.kind === 'sectionTitle';

export const focusableIndexFrom = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    start: number,
    step: 1 | -1,
) => {
    for (let index = start; index >= 0 && index < rows.length; index += step) {
        if (rowIsFocusable(rows[index])) {
            return index;
        }
    }
    return -1;
};

// The first row below the section titles, or the first title when every
// section is folded.
export const firstIndexToFocus = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
) => {
    const firstBelowTitles = rows.findIndex(
        (row) => !isSectionTitle(row) && rowIsFocusable(row),
    );
    return firstBelowTitles === -1
        ? focusableIndexFrom(rows, rowIsFocusable, 0, 1)
        : firstBelowTitles;
};

const sectionTitleIndexAbove = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    index: number,
) => {
    for (let above = index - 1; above >= 0; above -= 1) {
        if (isSectionTitle(rows[above])) {
            return rowIsFocusable(rows[above]) ? above : -1;
        }
    }
    return -1;
};

const focusOrNothing = (index: number): ListKeyAction | null =>
    index === -1 ? null : { kind: 'focus', index };

const forwardAction = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    index: number,
): ListKeyAction | null => {
    const row = rows[index];
    if (!isSectionTitle(row) || !row.canToggle) {
        return null;
    }
    if (!row.isOpen) {
        return { kind: 'click', index };
    }
    const next = rows[index + 1];
    return next && !isSectionTitle(next) && rowIsFocusable(next)
        ? { kind: 'focus', index: index + 1 }
        : null;
};

const backAction = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    index: number,
): ListKeyAction | null => {
    const row = rows[index];
    if (isSectionTitle(row)) {
        return row.canToggle && row.isOpen ? { kind: 'click', index } : null;
    }
    return focusOrNothing(sectionTitleIndexAbove(rows, rowIsFocusable, index));
};

// Forward and back follow the reading direction, as in a tree view.
export const listKeyAction = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    index: number,
    key: string,
    isRightToLeft: boolean,
): ListKeyAction | null => {
    const forwardKey = isRightToLeft ? 'ArrowLeft' : 'ArrowRight';
    const backKey = isRightToLeft ? 'ArrowRight' : 'ArrowLeft';

    switch (key) {
        case 'ArrowDown':
            return focusOrNothing(
                focusableIndexFrom(rows, rowIsFocusable, index + 1, 1),
            );
        case 'ArrowUp':
            return focusOrNothing(
                focusableIndexFrom(rows, rowIsFocusable, index - 1, -1),
            );
        case 'Home':
            return focusOrNothing(
                focusableIndexFrom(rows, rowIsFocusable, 0, 1),
            );
        case 'End':
            return focusOrNothing(
                focusableIndexFrom(rows, rowIsFocusable, rows.length - 1, -1),
            );
        case forwardKey:
            return forwardAction(rows, rowIsFocusable, index);
        case backKey:
            return backAction(rows, rowIsFocusable, index);
        default:
            return null;
    }
};
